import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FaUserCircle } from "react-icons/fa";
import { MdLocationOn } from "react-icons/md";
import { FormatLocalDateTime } from "./PageFormats";

export default function PageHeader({ onLogout }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileLocationOpen, setIsMobileLocationOpen] = useState(false);

  const locationData = localStorage.getItem("userLocation");
  const parsedLocationData = JSON.parse(locationData);

  const navItems = [
    { navLabel: "Dashboard", navLink: "/dashboard", navColor: "bg-sky-600" },
    {
      navLabel: "Escalations",
      navLink: "/escalations",
      navColor: "bg-red-600",
    },
    {
      navLabel: "Admin",
      navLink: "/admin",
      navColor: "bg-gray-400",
    },
  ];

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="bg-[#f2f9fd] border-b border-gray-300 shadow-md">
      <div className="relative flex items-center justify-between gap-3 px-4 py-2 mx-2 md:px-5 md:py-3 xl:px-8 xl:py-4 flex-nowrap">
        {/* LEFT: Logo */}
        <div className="flex items-center flex-shrink-0">
          <h1 className="text-lg font-bold truncate select-none md:text-xl xl:text-2xl">
            <span className="text-sky-600">PERMANENTE</span>
            <span> MEDICINE</span>
            <sup className="text-sm">®</sup>
          </h1>
          {/* Location: Minimal icon on mobile, dropdown on xl+ */}
          <div className="relative ml-2 md:hidden lg:hidden xl:hidden">
            <MdLocationOn
              className="w-5 h-5 text-black cursor-pointer"
              onClick={() => setIsMobileLocationOpen((prev) => !prev)}
            />
            {isMobileLocationOpen && (
              <div className="absolute left-0 z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-md">
                {Array.isArray(parsedLocationData) &&
                parsedLocationData.length > 0 ? (
                  <select
                    className="px-2 py-1 text-black bg-white outline-none"
                    defaultValue={parsedLocationData[0]}
                    onClick={() => setIsMobileLocationOpen(false)}
                  >
                    {parsedLocationData.map((loc, idx) => (
                      <option key={idx} value={loc}>
                        {loc}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="px-3 py-2 text-sm">Roseville</div>
                )}
              </div>
            )}
          </div>
          <div className="hidden ml-2 lg:block md:block xl:block">
            <div className="flex items-center justify-center h-10 px-2 py-1 bg-gray-300 border border-black rounded-md">
              <MdLocationOn className="w-5 h-5 mr-1 text-black" />
              {Array.isArray(parsedLocationData) &&
              parsedLocationData.length > 0 ? (
                <select
                  className="text-black bg-gray-300 outline-none"
                  defaultValue={parsedLocationData[0]}
                >
                  {parsedLocationData.map((loc, idx) => (
                    <option key={idx} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
              ) : (
                <span>Roseville</span>
              )}
            </div>
          </div>
        </div>

        {/* MIDDLE: Navigation aligned left, hidden on mobile */}
        <nav
          className="justify-center flex-grow hidden space-x-6 xl:flex scrollbar-hide flex-nowrap xl:ml-4 xl:mr-4"
          aria-label="Primary"
        >
          {navItems.map((nav, idx) => (
            <NavItem
              key={idx}
              to={nav.navLink}
              label={nav.navLabel}
              buttonColor={nav.navColor}
              isActiveFunc={(match, location) =>
                location.pathname.startsWith(nav.navLink)
              }
              closeMobileMenu={closeMobileMenu}
            />
          ))}
        </nav>

        {/* RIGHT: Date, Profile, Logout for desktop (xl and above) */}
        <div className="items-center flex-shrink-0 hidden space-x-3 xl:flex md:space-x-4">
          <FormatLocalDateTime />
          <div className="text-3xl text-gray-600 select-none md:text-4xl shrink-0">
            <FaUserCircle />
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center justify-center h-10 px-2 py-2 font-semibold text-center text-white transition-shadow duration-300 bg-[#333446] border border-black rounded-md w-28"
            aria-label="Logout"
          >
            Logout
          </button>
        </div>

        {/* Mobile Menu Toggle + Profile Icon */}
        <div className="absolute flex items-center space-x-3 transform -translate-y-1/2 xl:hidden right-4 top-1/2">
          <div className="hidden sm:block xl:hidden">
            <FormatLocalDateTime />
          </div>

          <div className="text-4xl text-gray-600 select-none shrink-0">
            <FaUserCircle />
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="mb-2 text-4xl text-gray-600 focus:outline-none"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "✖" : "☰"}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <nav
          className="flex flex-col px-4 py-2 space-y-2 bg-white border-t border-gray-300 xl:hidden"
          aria-label="Mobile Primary Navigation"
        >
          {navItems.map((nav, idx) => (
            <NavItem
              key={idx}
              to={nav.navLink}
              label={nav.navLabel}
              isActiveFunc={(match, location) =>
                location.pathname.startsWith(nav.navLink)
              }
              isMobile={true}
              closeMobileMenu={closeMobileMenu}
            />
          ))}
          {/* Logout Option in Mobile Menu */}
          <button
            onClick={() => {
              handleLogout();
              closeMobileMenu();
            }}
            className="block px-4 py-2 text-base font-semibold text-left text-gray-700 rounded-md hover:bg-blue-100"
          >
            Logout
          </button>
        </nav>
      )}
    </header>
  );
}

const NavItem = ({
  to,
  label,
  buttonColor,
  isActiveFunc,
  isMobile,
  closeMobileMenu,
}) => {
  const location = useLocation();

  const handleClick = () => {
    if (isMobile) {
      closeMobileMenu();
    }
  };

  return (
    <NavLink
      to={to}
      onClick={handleClick}
      className={({ isActive }) => {
        const active = isActiveFunc ? isActiveFunc(null, location) : isActive;
        const baseStyles =
          isMobile && "block px-4 py-2 rounded-md font-semibold text-base";

        return isMobile
          ? active
            ? `${baseStyles} bg-sky-600 text-white`
            : `${baseStyles} text-gray-700 hover:bg-blue-100`
          : `${buttonColor} w-32 h-10 flex items-center justify-center border-black text-white text-center border-2 rounded-md px-2 py-2 font-semibold transition-shadow duration-300 ${
              active
                ? buttonColor && buttonColor.includes("bg-sky-600")
                  ? "shadow-[0_0_10px_rgba(2,132,199,0.8)]"
                  : buttonColor && buttonColor.includes("bg-red-600")
                  ? "shadow-[0_0_10px_rgba(220,38,38,0.8)]"
                  : buttonColor && buttonColor.includes("bg-gray-400")
                  ? "shadow-[0_0_10px_rgba(156,163,175,0.8)]"
                  : ""
                : "border-transparent"
            }`;
      }}
      title={label}
      aria-current={isActiveFunc ? "page" : undefined}
    >
      {label}
    </NavLink>
  );
};
